import Icon, { IconName } from './Icon'
import { type ButtonHTMLAttributes } from 'react'
import { Text, TextSize, TextVariant } from '~/components/ui/Text'
import { cn } from '~/utils/common'

/**
 * Button component variants
 */
export type ButtonVariant =
  | 'primary' // Main action buttons
  | 'secondary' // Alternative action buttons
  | 'outline' // Bordered buttons with transparent background
  | 'ghost' // Text-only buttons with hover state
  | 'link' // Text-only buttons that look like links
  | 'success' // Positive action buttons
  | 'warning' // Cautionary action buttons
  | 'danger' // Destructive action buttons
  | 'light' // Light action buttons

/**
 * Button component sizes
 */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

/**
 * Button component props
 */
export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconName
  iconPosition?: 'start' | 'end'
  isLoading?: boolean
  isDisabled?: boolean
  block?: boolean
  className?: string
  textClassName?: string
  children?: React.ReactNode
}

/**
 * Button component variant classes
 */
const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-10 hover:bg-primary-10/90 active:bg-primary-10/80 border border-transparent transition-colors',
  secondary:
    'bg-dark hover:bg-dark/90 active:bg-dark/80 border border-transparent transition-colors',
  outline:
    'border border-light bg-transparent hover:bg-light/10 active:bg-light/20 transition-colors',
  ghost: 'bg-transparent hover:bg-light-20/10 active:bg-light-20/20 transition-colors',
  link: 'bg-transparent underline hover:bg-light-20/10 active:bg-light-20/20 transition-colors',
  success:
    'bg-success-10 hover:bg-success-10/90 active:bg-success-10/80 border border-transparent transition-colors',
  warning:
    'bg-warning-10 hover:bg-warning-10/90 active:bg-warning-10/80 border border-transparent transition-colors',
  danger:
    'bg-danger-10 hover:bg-danger-10/90 active:bg-danger-10/80 border border-transparent transition-colors',
  light:
    'bg-light hover:bg-light/90 active:bg-light/80 border border-transparent transition-colors',
}

/**
 * Button component size classes
 */
const BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  xs: 'py-1 px-2',
  sm: 'py-1.5 px-3',
  md: 'py-2 px-4',
  lg: 'py-2.5 px-5',
  xl: 'py-3 px-6',
}

/**
 * Text size mapping for button sizes
 */
const BUTTON_TEXT_SIZE: Record<ButtonSize, TextSize> = {
  xs: 'sm',
  sm: 'md',
  md: 'lg',
  lg: 'lg',
  xl: 'xl',
}

/**
 * Button component text variant classes based on button variant
 */
const BUTTON_TEXT_VARIANT: Record<ButtonVariant, TextVariant> = {
  primary: 'default',
  secondary: 'default',
  outline: 'default',
  ghost: 'default',
  link: 'link',
  success: 'default',
  warning: 'default',
  danger: 'default',
  light: 'dark',
}

/**
 * Enhanced Button component with Tailwind CSS styling
 *
 * @param props - Button component props
 * @returns React component
 */
export function Button({
  style,
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'start',
  isLoading = false,
  isDisabled = false,
  disabled,
  block = false,
  className,
  textClassName,
  children,
  ...rest
}: ButtonProps) {
  const isButtonDisabled = disabled || isDisabled || isLoading

  // Combine all classes based on props
  const buttonClasses = cn(
    'flex flex-row items-center justify-center self-start',
    BUTTON_VARIANT_CLASSES[variant],
    BUTTON_SIZE_CLASSES[size],
    isButtonDisabled && 'opacity-50 cursor-not-allowed',
    block ? 'w-full' : 'w-auto',
    className,
  )

  // Get the text size based on button size
  const textSize = BUTTON_TEXT_SIZE[size]

  // Get the text variant based on button variant
  const textVariant = BUTTON_TEXT_VARIANT[variant]

  // Icon size based on button size
  const getIconSize = () => {
    switch (size) {
      case 'xs':
        return 12
      case 'sm':
        return 14
      case 'md':
        return 16
      case 'lg':
        return 18
      case 'xl':
        return 20
      default:
        return 16
    }
  }

  // Get icon color based on variant
  const getIconColor = () => {
    if (variant === 'light') return '#000000'
    if (variant === 'outline' || variant === 'ghost' || variant === 'link') return '#F5F5F5'
    return '#FFFFFF'
  }

  const iconColor = getIconColor()

  return (
    <button
      type='button'
      className={buttonClasses}
      style={style}
      disabled={isButtonDisabled}
      {...rest}>
      {isLoading && (
        <svg
          className='animate-spin mr-2'
          style={{ width: getIconSize(), height: getIconSize() }}
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'>
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke={iconColor}
            strokeWidth='4'
          />
          <path
            className='opacity-75'
            fill={iconColor}
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          />
        </svg>
      )}

      {!isLoading && icon && iconPosition === 'start' && (
        <Icon name={icon} size={getIconSize()} color={iconColor} />
      )}

      <Text variant={textVariant} size={textSize} className={cn(textClassName)}>
        {children}
      </Text>

      {!isLoading && icon && iconPosition === 'end' && (
        <Icon name={icon} size={getIconSize()} color={iconColor} />
      )}
    </button>
  )
}
